<template>
  <v-app>
    <div class="app-container">
      <v-main class="bg main-content">
        <!-- Normal NavBar -->
        <NavBar v-if="showNavBar" />

        <!-- KYC NavBar -->
        <NavBarKYC v-if="showKycNavBar" />

        <!-- Layout with Sidebar for dashboard pages -->
        <div v-if="showNavBar">
          <SideBar />
          <div class="flex-grow-1">
            <router-view />
          </div>
        </div>

        <!-- Router view without sidebar for other pages -->
        <router-view v-else />
      </v-main>

      <FooterBar />
    </div>

    <!-- Global Session Timeout Dialog (moved outside v-main) -->
    <DialogSessionTimeout
      :dialog="showSessionTimeoutDialog"
      @login="handleSessionTimeout"
      @update:dialog="showSessionTimeoutDialog = $event"
    />

    <!-- Mobile Warning Overlay -->
    <div v-if="isMobileWarningVisible" class="mobile-warning-overlay">
      <div class="mobile-warning-card">
        <img :src="zetrixLogo" alt="Zetrix Logo" class="mobile-warning-logo" />
        <h2 class="mobile-warning-title">Desktop Recommended</h2>
        <p class="mobile-warning-message">
          This platform is optimised for desktop use. For the best experience, please open it on a desktop or laptop browser.
        </p>
        <button class="mobile-warning-btn-primary" @click="copyLinkToDesktop">
          {{ linkCopied ? 'Link Copied!' : 'Open on Desktop' }}
        </button>
        <button class="mobile-warning-btn-link" @click="dismissMobileWarning">
          Continue Anyway
        </button>
      </div>
    </div>
  </v-app>
</template>

<script>
import { defineComponent, watch } from "vue";
import NavBar from "./components/appBar/navBar.vue";
import NavBarKYC from "./components/appBar/navBarKyc.vue";
import SideBar from "./components/appBar/sideBar.vue";
import FooterBar from "./components/appBar/footerBar.vue";
import { eventBus } from '@/utils/eventBus';
import DialogSessionTimeout from '@/components/dialogs/dialogSessionTimeout.vue';
import { isMobile } from '@/utils/util';
import zetrixLogo from '@/assets/images/Zetrix-Logo-small-black.png';

export default defineComponent({
  name: "App",
  components: { NavBar, NavBarKYC, SideBar, FooterBar, DialogSessionTimeout },
  data() {
    return {
      isConnected: false,
      showSessionTimeoutDialog: false,
      mobileWarningDismissed: !!sessionStorage.getItem("mobileWarningDismissed"),
      linkCopied: false,
      zetrixLogo
    };
  },
  computed: {
    showNavBar() {
      const excludedPaths = ["/kyc-application", "/kyc-status", "/"];
      return this.isConnected && !excludedPaths.includes(this.$route.path);
    },
    showKycNavBar() {
      const kycPaths = ["/kyc-application", "/kyc-status"];
      return this.isConnected && kycPaths.includes(this.$route.path);
    },
    isMobileWarningVisible() {
      return isMobile() && !this.mobileWarningDismissed;
    }
  },
  mounted() {
    this.checkConnectionStatus();
    window.addEventListener("account-connected", this.checkConnectionStatus);
    window.addEventListener("storage", this.checkConnectionStatus);
    
    // Watch eventBus using Composition API watch
    watch(
      () => eventBus.showSessionTimeout,
      (newVal) => {
        this.showSessionTimeoutDialog = newVal;
      },
      { immediate: true }
    );
  },
  unmounted() {
    window.removeEventListener("account-connected", this.checkConnectionStatus);
    window.removeEventListener("storage", this.checkConnectionStatus);
  },
  methods: {
    checkConnectionStatus() {
      this.isConnected = !!sessionStorage.getItem("zetrixAccount");
    },
    dismissMobileWarning() {
      sessionStorage.setItem("mobileWarningDismissed", "true");
      this.mobileWarningDismissed = true;
    },
    copyLinkToDesktop() {
      navigator.clipboard.writeText(window.location.href)
        .then(() => {
          this.linkCopied = true;
          setTimeout(() => { this.linkCopied = false; }, 2500);
        })
        .catch(() => {
          window.prompt("Copy this link to open on desktop:", window.location.href);
        });
    },
    handleSessionTimeout() {
      // Clear storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Hide dialog
      this.showSessionTimeoutDialog = false;
      eventBus.hideSessionTimeout();
      
      // Redirect to login
      this.$router.push('/');
      
      // Reload page to clear any cached state
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }
});
</script>

<style>
.bg {
  background-color: #F8F9F9;
}

.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.main-content {
  flex: 1;
}

/* Mobile Warning Overlay */
.mobile-warning-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}

.mobile-warning-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 360px;
  width: 100%;
}

.mobile-warning-logo {
  width: 140px;
  margin-bottom: 32px;
}

.mobile-warning-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 12px;
}

.mobile-warning-message {
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  margin-bottom: 32px;
}

.mobile-warning-btn-primary {
  width: 100%;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(180deg, #DC404D 0%, #C22328 99.99%);
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 16px;
}

.mobile-warning-btn-link {
  background: none;
  border: none;
  color: #C22328;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  padding: 4px 0;
}

.mobile-warning-btn-link:hover {
  color: #DC404D;
}
</style>